import { useState, useEffect } from "react";
import {
  getUsers,
  addAnnotator,
  removeAnnotator,
  reactivateAnnotator,
} from "../services/api";
import Sidebar from "./Sidebar";
import ConfirmModal from "./ConfirmModal";
import { useEventLogger } from '../hooks/useEventLogger';

export default function UserManagement({ user, onPageChange }) {
  const { logNavigation, logLogout } = useEventLogger(user);
  const [users, setUsers] = useState({
    admins: [],
    annotators: [],
    inactive_annotators: [],
  });
  const [newEmail, setNewEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    email: "",
  });

  // Email validation regex
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const validateEmail = (email) => {
    return emailRegex.test(email);
  };

  // Auto-hide error after 3 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (err) {
      if (err.response?.status === 403) {
        // User is not admin, redirect to dashboard
        onPageChange("dashboard");
        return;
      }
      setError("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleAddAnnotator = async (e) => {
    e.preventDefault();
    if (!newEmail) return;

    // Validate email format
    if (!validateEmail(newEmail)) {
      setError("Please enter a valid email address");
      return;
    }

    try {
      await addAnnotator(newEmail);
      setNewEmail("");
      setError(""); // Clear any previous errors
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to add user");
    }
  };

  const handleRemoveAnnotator = async (email) => {
    setConfirmModal({ isOpen: true, email });
  };

  const confirmRemove = async () => {
    try {
      await removeAnnotator(confirmModal.email);
      loadUsers();
      setConfirmModal({ isOpen: false, email: "" });
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to deactivate user");
    }
  };

  const handleReactivate = async (email) => {
    try {
      await reactivateAnnotator(email);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reactivate user");
    }
  };

  const cancelRemove = () => {
    setConfirmModal({ isOpen: false, email: "" });
  };

  if (loading) return <div className="user-management">Loading...</div>;

  return (
    <div className="workspace">
      <Sidebar user={user} currentPage="users" onPageChange={onPageChange} onLogout={logLogout} onNavigation={logNavigation} />
      <main className="annotator">
        <div className="user-management">
          <h1>User Management</h1>

          {error && <div className="error">{error}</div>}

          <div className="user-section">
            <h2>Add New Annotator</h2>
            <form onSubmit={handleAddAnnotator} className="add-user-form">
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Enter email address"
                required
              />
              <button type="submit">Add Annotator</button>
            </form>
          </div>

          <div className="user-section">
            <h2>Admins ({users.admins.length})</h2>
            <div className="user-list">
              {users.admins.map((admin) => (
                <div key={admin.email} className="user-item admin">
                  <span>{admin.email}</span>
                  <span className="role">Admin</span>
                </div>
              ))}
            </div>
          </div>

          <div className="user-section">
            <h2>Annotators ({users.annotators.length})</h2>
            <div className="user-list">
              {users.annotators.map((annotator) => (
                <div key={annotator.email} className="user-item">
                  <span>{annotator.email}</span>
                  <button
                    onClick={() => handleRemoveAnnotator(annotator.email)}
                    className="remove-btn"
                  >
                    Deactivate
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="user-section">
            <h2>Inactive Annotators ({users.inactive_annotators.length})</h2>
            <div className="user-list">
              {users.inactive_annotators.map((annotator) => (
                <div
                  key={annotator.email}
                  className="user-item"
                  style={{ opacity: 0.6 }}
                >
                  <span>{annotator.email}</span>
                  <button
                    onClick={() => handleReactivate(annotator.email)}
                    style={{
                      background: "#28a745",
                      color: "white",
                      border: "none",
                      padding: "4px 12px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "0.8rem",
                    }}
                  >
                    Reactivate
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onConfirm={confirmRemove}
        onCancel={cancelRemove}
        title="Deactivate Annotator"
        message={`Are you sure you want to deactivate ${confirmModal.email}? They will not be able to login but their data will be preserved.`}
      />
    </div>
  );
}
